import { useRef, useState } from 'react';

import type { GenParam } from '@ai-studio/shared';

import { NumberField, PropRow } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { lockedSize } from '../../lib/generation.js';
import './size-control.css';

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/**
 * Width x Height control of a size parameter (final sprite size, hi-res target): two number fields
 * that accept any whole size inside the bounds, preset buttons, an aspect lock and (when allowed)
 * an Auto button for "use the Size setting".
 */
export function SizeControl({ param, width, height, onChange }: { param: GenParam; width: number; height: number; onChange: (w: number, h: number) => void }) {
  const lo = param.min ?? 1;
  const hi = param.max ?? 16384;
  const [lock, setLock] = useState(false);
  const ratio = useRef(1);
  const isAuto = !!param.auto && width === 0 && height === 0;
  const w = isAuto ? undefined : width;
  const h = isAuto ? undefined : height;

  function toggleLock() {
    if (!lock && width > 0 && height > 0) ratio.current = height / width;
    setLock(!lock);
  }
  function edit(side: 'w' | 'h', value: number) {
    if (!Number.isFinite(value)) return;
    const cw = isAuto ? lo : width;
    const ch = isAuto ? lo : height;
    if (lock) return onChange(...lockedSize(side, value, ratio.current, lo, hi));
    onChange(side === 'w' ? clamp(Math.round(value), lo, hi) : cw, side === 'h' ? clamp(Math.round(value), lo, hi) : ch);
  }
  function preset(pw: number, ph: number) {
    if (ph > 0) ratio.current = ph / pw;
    onChange(pw, ph);
  }
  const tooBig = param.maxPixels !== undefined && width * height > param.maxPixels;

  return (
    <PropRow label={param.label} hint={param.hint}>
      <div className="size-control">
        <div className="size-control-row">
          <NumberField value={w} onChange={(v) => edit('w', v)} min={lo} max={hi} step={1} integer title={`Width in pixels (${lo}-${hi}; type any value)`} />
          <span className="muted">x</span>
          <NumberField value={h} onChange={(v) => edit('h', v)} min={lo} max={hi} step={1} integer title={`Height in pixels (${lo}-${hi}; type any value)`} />
          <button type="button" className={`ibtn${lock ? ' active' : ''}`} aria-pressed={lock} title={lock ? 'Aspect ratio locked' : 'Lock the aspect ratio'} onClick={toggleLock}>
            <Icon name="link" size={13} />
          </button>
          {param.auto && (
            <button type="button" className={`ghost small${isAuto ? ' active' : ''}`} title="Use the Size setting (longest side) instead of an exact size" onClick={() => preset(0, 0)}>
              Auto
            </button>
          )}
        </div>
        {param.presets && (
          <div className="size-control-presets">
            {param.presets.map(([pw, ph]) => (
              <button key={`${pw}x${ph}`} type="button" className={`ghost small${width === pw && height === ph ? ' active' : ''}`} title={`${pw} x ${ph}`} onClick={() => preset(pw, ph)}>
                {pw === ph ? pw : `${pw}x${ph}`}
              </button>
            ))}
          </div>
        )}
        {tooBig && <span className="gen-error">{`${Math.round((width * height) / 1e6)} megapixels is over the ${(param.maxPixels ?? 0) / 1e6} MP limit.`}</span>}
      </div>
    </PropRow>
  );
}
